import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Image, { IImage } from 'tbk-components/src/components/Image';
import Nav, { NavItem } from 'tbk-components/src/components/Nav';
import MMNav, { MMNavItem } from '../MegaMenus/Nav';
import Collapse from 'bootstrap/js/dist/collapse';
import Offcanvas from 'bootstrap/js/dist/offcanvas';
import SearchModal, { ISearchModalProps } from '../SearchModal';
import SocialIcons, { ISocialIconsProps } from '../SocialIcons';

// Represents a link in the language selector drop-down
export type LanguageNavItem = {
	href: string;
	label: string;
	target?: string;
	icon?: string;
	code?: string;
	locale?: string;
};

export interface IHeaderProps extends IBaseElementProps {
	logo?: IImage;
	logoURL?: string;
	headerBusinessUnit: string;
	businessUnitURL?: string;
	mainNav?: MMNavItem[];
	mobileNav?: NavItem[];
	businessUnitSelectionNav?: MMNavItem[];
	subNavName?: string;
	headerSubNavigation?: NavItem[];
	contactButton?: ILink;
	searchButton?: ILink;
	activeLanguage?: LanguageNavItem;
	languageSelectionNav: LanguageNavItem[];
	homeURL?: string;
	homeURLText?: string;
	topMenuNav?: MMNavItem[];
	/*
	 * Will turn the header items white when stickied on desktop.
	 */
	invert?: boolean;
	/*
	 * Will turn the button color to secondary, for example.
	 */
	styleInvert?: boolean;
	/*
	 * @noUI
	 */
	searchModal?: ISearchModalProps;
	socialIcons?: ISocialIconsProps;
}

const Header: React.FC<IHeaderProps> = ({
	className,
	classNames = [],
	logo,
	logoURL,
	headerBusinessUnit,
	businessUnitURL,
	topMenuNav,
	mainNav,
	mobileNav,
	businessUnitSelectionNav,
	subNavName,
	headerSubNavigation,
	contactButton,
	searchButton,
	homeURL,
	homeURLText = 'Go to Calian Home',
	activeLanguage,
	languageSelectionNav,
	invert = false,
	searchModal,
	styleInvert = false,
	socialIcons = [],
}) => {
	// Start of sticky positioning, collapse and off canvas code taken from default ba3e Navbar functionality.
	const [navLanguage, setNavLanguage] = useState('en');
	const [showSearchModal, setShowSearchModal] = useState(false);
	const ref = useRef(null);
	const collapseRef = useRef(null);
	const offcanvasRef = useRef(null);
	const headerRef = useRef(null);

	useEffect(() => {
		let currentPageLanguage = document.documentElement.lang;
		setNavLanguage(currentPageLanguage);

		if (!ref.current) {
			return;
		}

		// @ts-ignore
		const collapse = new Collapse(collapseRef.current, {
			toggle: false,
		});

		// @ts-ignore
		const offCanvas = new Offcanvas(offcanvasRef.current);

		const navbar = ref.current! as HTMLDivElement;

		const alertBar = document.querySelector(
			'[data-component="AlertBar"]',
		) as HTMLDivElement;

		if (alertBar) {
			document.body.classList.add('alert-bar-visible');
			alertBar
				?.querySelector('.btn-close')
				?.addEventListener('click', () => {
					headerRef.current.style.top = '';
				});
		}

		const nudgeHeader = () => {
			if (window.innerWidth >= 576) {
				if (alertBar) {
					headerRef.current.style.top = alertBar.offsetHeight + 'px';
				}
			} else {
				headerRef.current.style.top = '';
			}

			if (window.innerWidth >= 1200) {
				offCanvas.hide();
			}
		};

		const handleScroll = () => {
			if (window.scrollY > navbar.offsetTop) {
				document.body.classList.add('navbar-sticky');
			} else {
				document.body.classList.remove('navbar-sticky');
			}
		};

		const mainDesktopNav = ref.current.querySelector(
			'.calian-header__main-nav',
		);

		const mainDesktopOverflowNav = ref.current.querySelector(
			'.calian-header__main-nav--overflow',
		);

		const createOverflowDropdown = () => {
			let maxHeight = 40;
			let mainDesktopNavHeight = mainDesktopNav.clientHeight;
			if (mainDesktopNavHeight > maxHeight) {
				mainDesktopOverflowNav.classList.remove('d-none');
				while (mainDesktopNavHeight > maxHeight) {
					const lastChild = mainDesktopNav.lastElementChild;
					mainDesktopOverflowNav
						.querySelector('.dropdown-menu')
						.prepend(lastChild);
					mainDesktopNavHeight = mainDesktopNav.clientHeight;
				}
			}

			if (window.innerWidth >= 1200) {
				offCanvas.hide();
			}
		};

		const collapseElementList: HTMLAnchorElement[] = [].slice.call(
			document.querySelectorAll(
				'.calian-header__cta-language-select-mobile .calian-header__business-unit-select-nav--mobile .calian-mega-menus__top .calian-mega-menus__navs >ul >li >.nav-link',
			),
		);

		const collapseItems: Collapse[] = [];
		const handleCollapse = (
			eventType: 'addEventListener' | 'removeEventListener',
		) => {
			collapseElementList.forEach((collapseToggleEl, index) => {
				const collapseEl =
					collapseToggleEl.nextElementSibling as HTMLElement;

				if (!collapseEl) {
					return;
				}

				const collapseParent = collapseToggleEl.closest('.nav-item');
				let collapse = collapseItems[index];

				if (!collapse) {
					collapse = new Collapse(collapseEl, {
						toggle: false,
					});

					collapseItems.push(collapse);

					collapseParent.classList.add('collapse-menu');
					collapseEl.classList.add('collapse');
				}

				collapseToggleEl[eventType]('click', (e) => {
					e.stopPropagation();
					e.preventDefault();
					collapse && collapse.toggle();
				});

				collapseEl[eventType]('show.bs.collapse', (e) => {
					e.stopPropagation();
					collapseParent?.classList.add('collapse-open');
				});

				collapseEl[eventType]('hide.bs.collapse', (e) => {
					e.stopPropagation();
					collapseParent?.classList.remove('collapse-open');
				});
			});
		};

		function addHeroTopSpace() {
			if (headerSubNavigation) {
				document.body.classList.add('has-sub-nav');
			}
		}

		handleCollapse('addEventListener');
		handleScroll();
		nudgeHeader();

		window.addEventListener('scroll', handleScroll);
		window.addEventListener('resize', nudgeHeader);
		window.addEventListener('load', addHeroTopSpace);

		return () => {
			window.removeEventListener('scroll', handleScroll);

			handleCollapse('removeEventListener');
			window.removeEventListener('resize', nudgeHeader);

			window.removeEventListener('load', addHeroTopSpace);

			if (alertBar) {
				document.body.classList.remove('alert-bar-visible');
				alertBar
					.querySelector('.btn-close')
					.removeEventListener('click', () => {
						headerRef.current.style.top = '';
					});
			}
		};
	}, []);

	useLayoutEffect(() => {
		const scrollToTarget = (hash) => {
			const scrollTop = document.documentElement.scrollTop;
			const target = document.getElementById(hash) as HTMLElement;
			if (!target) {
				return;
			}
			const targetRectTop = target.getBoundingClientRect().top;
			const targetH = targetRectTop + scrollTop;
			const headerH = (
				document.querySelector('header.calian-header') as HTMLElement
			).getBoundingClientRect().height;
			const scrollH = targetH - headerH;

			window.scrollTo(0, scrollH);
		};
		const linkInit = () => {
			const hashLinks = document.querySelectorAll(
				'a[href*="#"]',
			) as NodeListOf<HTMLAnchorElement>;
			const pdfHash = /#asset:\d+:url/;
			const pagePath = window.location.pathname;
			const pageHash = window.location.hash;

			if (pageHash && !pdfHash.test(pageHash)) {
				scrollToTarget(pageHash.replace('#', ''));
			}

			hashLinks.forEach((a) => {
				const hashFromHref = a.getAttribute('href').split('#')[1];
				const linkPath = a.pathname;

				if (
					hashFromHref &&
					linkPath &&
					linkPath === pagePath &&
					!pdfHash.test(hashFromHref) &&
					!a.hasAttribute('data-bs-toggle')
				) {
					a.addEventListener('click', (e) => {
						e.preventDefault();
						scrollToTarget(`${hashFromHref}`);

						// Open accordion tab if exists
						const btn = document.querySelector(
							`button[id="${hashFromHref}"]`,
						) as HTMLButtonElement;
						if (btn) {
							btn.click();
						}
					});
				}
			});
		};

		window.addEventListener('load', linkInit);
		window.addEventListener('resize', linkInit);

		return () => {
			window.removeEventListener('load', linkInit);
			window.removeEventListener('resize', linkInit);
		};
	}, []);

	const languageNav = (
		<ul className="nav justify-content-start calian-header__language-select-nav">
			<li className="nav-item dropdown">
				<a
					className="nav-link dropdown-toggle"
					href="#"
					aria-expanded="false"
					role="button"
					data-bs-toggle="dropdown"
					data-bs-auto-close="true"
				>
					{activeLanguage?.label}
				</a>
				{languageSelectionNav && languageSelectionNav.length > 0 && (
					<ul className="dropdown-menu">
						{languageSelectionNav.map((languageNavItem, index) => {
							return (
								<li key={languageNavItem.code + '-' + index}>
									<a
										className="dropdown-item"
										href={languageNavItem.href}
										target={
											languageNavItem.target
												? languageNavItem.target
												: '_self'
										}
									>
										{languageNavItem.label}
									</a>
								</li>
							);
						})}
					</ul>
				)}
			</li>
		</ul>
	);

	// If mobileNav is not provided or empty, use mainNav as a backup
	if (!mobileNav) {
		mobileNav = mainNav;
	}
	// End of sticky positioning code taken from default ba3e Navbar
	return (
		<header
			className={
				className ||
				cn(
					'calian-header',
					invert && 'calian-header--invert',
					styleInvert && 'calian-header--style-invert',
					...classNames,
				)
			}
			ref={headerRef}
		>
			{topMenuNav && activeLanguage && (
				<div className="calian-header__topbar d-none d-xl-block border-bottom">
					<div className="calian-header__container px-3 container-fluid d-flex gap-3 justify-content-end">
						{topMenuNav && (
							<MMNav
								navItems={topMenuNav}
								collapseEnabled={false}
								dropdownEnabled={true}
								classNames={['calian-header__top-menu-nav']}
							/>
						)}
						{activeLanguage && languageNav}
					</div>
				</div>
			)}
			<div className="calian-header__container container-fluid">
				<nav
					className="calian-header__navbar navbar navbar-expand-xl navbar-white"
					ref={ref}
				>
					<div className="calian-header__navbar-container container-fluid">
						<a
							className="calian-header__navbar-brand navbar-brand"
							href={logoURL}
						>
							<Image {...logo} className="img-fluid" />
						</a>
						{headerBusinessUnit && (
							<p className="calian-header__business-unit">
								<a href={businessUnitURL}>
									{headerBusinessUnit}
								</a>
							</p>
						)}
						<div
							className="calian-header__navbar-collapse collapse collapse navbar-collapse"
							ref={collapseRef}
						>
							<div className="calian-header__navbar-left">
								{mainNav && (
									<>
										<MMNav
											navItems={mainNav}
											collapseEnabled={false}
											dropdownEnabled={true}
											classNames={[
												'calian-header__main-nav',
											]}
										/>
										<ul className="calian-header__main-nav--overflow nav d-none">
											<li className="nav-item dropdown">
												<a
													className="nav-link dropdown-toggle"
													href="#"
													aria-expanded="false"
													role="button"
													data-bs-toggle="dropdown"
													data-bs-auto-close="true"
												>
													{navLanguage === 'fr-ca'
														? 'Plus'
														: 'More'}
												</a>
												<ul className="dropdown-menu"></ul>
											</li>
										</ul>
									</>
								)}
							</div>
							<div className="calian-header__navbar-right">
								{!topMenuNav && activeLanguage && languageNav}
								{businessUnitSelectionNav && (
									<MMNav
										navItems={businessUnitSelectionNav}
										collapseEnabled={false}
										dropdownEnabled={true}
										classNames={[
											'calian-header__business-unit-select-nav',
										]}
									/>
								)}
							</div>
						</div>
					</div>
				</nav>
				<div className="calian-header__cta-search">
					{contactButton && (
						<Button
							className={cn(
								`calian-header__cta-btn btn btn-sm btn-${
									styleInvert ? 'secondary' : 'primary'
								}`,
							)}
							{...contactButton}
						/>
					)}
					{searchButton && (
						<a
							className="calian-header__search-btn btn"
							aria-label="Search"
							{...searchButton}
							onClick={(e) => {
								e.preventDefault();
								setShowSearchModal((s) => !s);
							}}
							aria-hidden="false"
						>
							<i className="calian-header__search-icon icon icon-magnifying-glass"></i>
						</a>
					)}
				</div>
				<button
					type="button"
					className="calian-header__navbar-toggler btn navbar-toggler"
					data-bs-toggle="offcanvas"
					data-bs-target=".offcanvas"
					aria-label="Toggle"
				>
					<i className="icon icon-menu-alternate"></i>
				</button>
			</div>
			{headerSubNavigation && (
				<div className="calian-header__sub-nav__container">
					<div className="calian-header__sub-nav__container-inner">
						{subNavName && <span>{subNavName}:</span>}
						<Nav
							navItems={headerSubNavigation}
							vertical={true}
							collapseEnabled={true}
							dropdownEnabled={false}
							classNames={['calian-header__sub-nav__items']}
						/>
					</div>
				</div>
			)}
			<div className="offcanvas offcanvas-end" ref={offcanvasRef}>
				<div className="offcanvas-header">
					<div className="offcanvas-header__upper">
						<div className="offcanvas-header__brand">
							<a
								className="calian-header__navbar-brand navbar-brand"
								href={logoURL}
							>
								<Image {...logo} className="img-fluid" />
							</a>
							{headerBusinessUnit && (
								<p className="calian-header__business-unit">
									<a href={businessUnitURL}>
										{headerBusinessUnit}
									</a>
								</p>
							)}
						</div>
						<button
							type="button"
							className="btn-close text-reset"
							data-bs-dismiss="offcanvas"
							aria-label="Close"
						></button>
					</div>
					<div className="calian-header__mobile-search">
						<form
							className="calian-header__mobile-search-form d-flex"
							action={homeURL}
							method="get"
						>
							<input
								name="s"
								className="form-control"
								type="text"
								placeholder="Search..."
								aria-label="Search"
							/>
							<button className="btn" type="submit">
								<i className="icon icon-magnifying-glass" />
								<span className="visually-hidden">Search</span>
							</button>
						</form>
					</div>
				</div>
				<div className="offcanvas-body">
					{mobileNav && (
						<Nav
							navItems={mobileNav}
							vertical={true}
							collapseEnabled={true}
							dropdownEnabled={false}
							classNames={['calian-header__main-nav--mobile']}
						/>
					)}
					{topMenuNav && (
						<Nav
							navItems={topMenuNav}
							collapseEnabled={true}
							dropdownEnabled={true}
							classNames={['calian-header__top-menu-nav--mobile']}
						/>
					)}
					<div className="calian-header__cta-language-select-mobile">
						<div className="calian-header__cta-language-select-mobile__all-calian">
							{businessUnitSelectionNav && (
								<Nav
									navItems={businessUnitSelectionNav}
									vertical={true}
									collapseEnabled={true}
									dropdownEnabled={false}
									classNames={[
										'calian-header__business-unit-select-nav--mobile',
									]}
								/>
							)}
						</div>
						{activeLanguage && (
							<div className="calian-header__cta-language-select-mobile__language">
								<div className="calian-header__language-select-nav--mobile">
									<a
										className="nav-link collapse-toggle"
										data-bs-toggle="collapse"
										href="#navMobileCollapse"
										role="button"
										aria-expanded="false"
										aria-controls="navMobileCollapse"
										onClick={(e) => {
											e.stopPropagation();
											e.preventDefault();
										}}
									>
										{activeLanguage?.label}
									</a>
								</div>

								<div
									className="collapse"
									id="navMobileCollapse"
								>
									<div className="calian-header__language-select-nav--mobile--content">
										<ul className="nav flex-column">
											{languageSelectionNav.map(
												(languageNavItem, index) => {
													return (
														<li
															className="nav-item"
															key={
																languageNavItem.code +
																'-' +
																index
															}
														>
															<a
																className="nav-link"
																href={
																	languageNavItem.href
																}
																target={
																	languageNavItem.target
																		? languageNavItem.target
																		: '_self'
																}
															>
																{
																	languageNavItem.label
																}
															</a>
														</li>
													);
												},
											)}
										</ul>
									</div>
								</div>
							</div>
						)}
						<div className="calian-header__cta-language-select-mobile__cta">
							{contactButton && (
								<Button
									className={cn(
										`calian-header__cta-btn--mobile btn btn-sm btn-${
											styleInvert
												? 'secondary'
												: 'primary'
										}`,
									)}
									{...contactButton}
								/>
							)}
						</div>
					</div>
					{homeURL && (
						<div className="px-3 mt-8">
							<Button
								size="sm"
								className="btn btn-primary btn-sm w-100 d-flex align-items-center justify-content-center gap-2"
								icon="caret-left"
								href={homeURL}
								label={homeURLText}
							/>
						</div>
					)}
				</div>
			</div>

			{showSearchModal && (
				<SearchModal
					{...searchModal}
					advancedSearchUrl={searchButton}
					onCancel={() => {
						setShowSearchModal(false);
					}}
				/>
			)}
		</header>
	);
};

export default Header;
